/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Renders a dashboard: its widgets, their data, and the edit affordances.
 *
 * Shared by the dashboard page and the home page, because "your home page" is
 * a dashboard — the only difference is the chrome around it. Keeping them one
 * component is what makes "set this dashboard as my home" a one-line change
 * rather than a second renderer to keep in step.
 *
 * All widgets are filled from one run request. See widgets/index.js for why.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { DeleteOutlineRounded, MoreVertRounded, SettingsRounded } from '@mui/icons-material';

import { useAuth } from 'auth/Auth';
import { useCurrency } from 'context/CurrencyContext';
import colors from 'styles/tokens/colors';
import DashboardGrid from './DashboardGrid';
import {
  ChartWidget, GreetingWidget, KpiWidget, ListWidget, TextWidget, WidgetFrame,
} from './widgets';

/** How long a dashboard's data is reused before a re-run. */
const STALE_AFTER_MS = 60_000;

export default function DashboardCanvas({ dashboard, editable, overrides,
                                          onLayoutChange, onRemoveWidget,
                                          onConfigureWidget, emptyState,
                                          hiddenKinds }) {
  const { axiosInstance, user } = useAuth();
  const { currency } = useCurrency();
  const navigate = useNavigate();

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState({ anchor: null, widget: null });
  const lastRun = useRef(0);

  const dashboardId = dashboard?.id;
  // Hidden only while viewing: in edit mode every widget is drawn, so a saved
  // arrangement still carries the hidden ones' positions.
  const widgets = useMemo(() => {
    const all = dashboard?.widgets ?? [];
    return editable || !hiddenKinds?.length
      ? all : all.filter((w) => !hiddenKinds.includes(w.widget_kind));
  }, [dashboard, editable, hiddenKinds]);

  const run = useCallback(async () => {
    if (!dashboardId) return;
    setLoading(true);
    try {
      const { data: response } = await axiosInstance.post(
        `/analytics/dashboards/${dashboardId}/run`, { overrides },
      );
      setData(Object.fromEntries(
        (response.widgets ?? []).map((w) => [w.grid_key, w]),
      ));
      lastRun.current = Date.now();
    } catch {
      // A whole-dashboard failure is the request itself failing; each widget
      // already carries its own error when only that widget failed.
      setData(Object.fromEntries(widgets.map((w) => [w.grid_key, {
        grid_key: w.grid_key, widget_kind: w.widget_kind,
        error: 'This dashboard could not be loaded.',
      }])));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, dashboardId, overrides, widgets]);

  useEffect(() => { run(); }, [run]);

  // Refresh when the tab comes back after being away a while. A dashboard left
  // open overnight showing yesterday's numbers with no indication is worse
  // than a brief loading state.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible'
          && Date.now() - lastRun.current > STALE_AFTER_MS) {
        run();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [run]);

  const renderWidget = useCallback((widget) => {
    const payload = data[widget.grid_key];
    const result = payload?.result;
    const title = payload?.title ?? widget.config?.title ?? 'Widget';
    const reportId = widget.report_id
      ?? (payload?.report_id ?? null);

    const body = (() => {
      switch (widget.widget_kind) {
        case 'kpi':
          return <KpiWidget result={result} config={widget.config} currency={currency} />;
        case 'report_chart':
          return (
            <ChartWidget
              result={result}
              config={{ ...(payload?.chart_config ?? {}), ...(widget.config ?? {}) }}
              chartType={payload?.chart_type ?? widget.config?.chart_type}
              currency={currency}
            />
          );
        case 'list':
          return <ListWidget result={result} currency={currency} />;
        case 'text':
          return <TextWidget config={widget.config} />;
        case 'greeting':
          return (
            <GreetingWidget
              config={widget.config}
              userName={user?.first_name ?? user?.name ?? ''}
            />
          );
        default:
          return (
            <Typography variant="caption" color="text.secondary">
              This widget type is not available in this version.
            </Typography>
          );
      }
    })();

    const chrome = widget.widget_kind === 'greeting';

    return (
      <Box sx={{ height: '100%', position: 'relative' }}>
        {editable && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setMenu({ anchor: e.currentTarget, widget }); }}
            sx={{
              position: 'absolute', top: 6, right: 6, zIndex: 2,
              backgroundColor: colors.background.white,
              border: '1px solid', borderColor: 'divider',
              '&:hover': { backgroundColor: colors.background.offWhite },
            }}
          >
            <MoreVertRounded sx={{ fontSize: 15 }} />
          </IconButton>
        )}
        {chrome ? (
          <Box className={editable ? 'widget-drag-handle' : undefined}
               sx={{ height: '100%', px: 2, cursor: editable ? 'move' : 'default' }}>
            {body}
          </Box>
        ) : (
          <WidgetFrame
            title={title}
            subtitle={result?.truncated
              ? `First ${result.limit.toLocaleString()} rows` : undefined}
            loading={loading && !payload}
            error={payload?.error}
            dragHandle={editable}
            onOpen={reportId ? () => navigate(`/reports/${reportId}`) : undefined}
          >
            <Box className="widget-no-drag" sx={{ height: '100%' }}>{body}</Box>
          </WidgetFrame>
        )}
      </Box>
    );
  }, [data, loading, editable, currency, user, navigate]);

  if (!widgets.length) {
    return emptyState ?? null;
  }

  return (
    <>
      <DashboardGrid
        layout={dashboard.layout}
        widgets={widgets}
        editable={editable}
        onLayoutChange={onLayoutChange}
        renderWidget={renderWidget}
      />

      <Menu
        anchorEl={menu.anchor} open={Boolean(menu.anchor)}
        onClose={() => setMenu({ anchor: null, widget: null })}
      >
        {onConfigureWidget && (
          <MenuItem onClick={() => {
            onConfigureWidget(menu.widget);
            setMenu({ anchor: null, widget: null });
          }}>
            <SettingsRounded fontSize="small" sx={{ mr: 1 }} /> Configure
          </MenuItem>
        )}
        <MenuItem
          onClick={() => { onRemoveWidget?.(menu.widget); setMenu({ anchor: null, widget: null }); }}
          sx={{ color: colors.error.text }}
        >
          <DeleteOutlineRounded fontSize="small" sx={{ mr: 1 }} /> Remove
        </MenuItem>
      </Menu>
    </>
  );
}

DashboardCanvas.propTypes = {
  dashboard: PropTypes.object,
  editable: PropTypes.bool,
  overrides: PropTypes.object,
  onLayoutChange: PropTypes.func,
  onRemoveWidget: PropTypes.func,
  onConfigureWidget: PropTypes.func,
  emptyState: PropTypes.node,
  hiddenKinds: PropTypes.arrayOf(PropTypes.string),
};
